/**
 * Recover from stale chunk references after a deploy.
 *
 * The app code-splits routes and canvas plugins into content-hashed chunks
 * (`_app-Dw4vYoss.js`). When a new version ships, the previous build's chunks
 * are replaced with freshly-hashed ones. A tab that was loaded *before* the
 * deploy still references the old hashes; the first lazy import after that
 * (route change, opening a canvas plugin) requests a file that no longer exists.
 * Firebase Hosting's SPA rewrite serves `index.html` for the 404, so the browser
 * receives HTML where it expected JS — Vite's preload helper then dispatches
 * `vite:preloadError` and the import rejects. That rejection bubbles to the
 * TanStack Router error boundary as "Failed to fetch dynamically imported module".
 *
 * The fix: reload once so the browser fetches a fresh `index.html` pointing at
 * the current chunk hashes. A short cooldown (persisted in sessionStorage, so it
 * survives the reload) prevents an infinite loop when the chunk is *genuinely*
 * gone — a bad/partial deploy or the user being offline. In that case the second
 * `preloadError` within the cooldown window is left alone, so the error surfaces
 * to the route error boundary and Sentry instead of reloading forever.
 */

const RELOAD_TS_KEY = "vite:preload-reload-ts";
const COOLDOWN_MS = 10_000;

let installed = false;

export function installChunkReload(): void {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  window.addEventListener("vite:preloadError", (event) => {
    let lastReload = 0;
    try {
      lastReload = Number(sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    } catch {
      // sessionStorage can throw in privacy modes / sandboxed iframes; if we
      // can't read the guard, fall through and attempt a single reload anyway.
    }

    const now = Date.now();
    if (now - lastReload < COOLDOWN_MS) {
      // Already reloaded recently and the chunk is still missing — this is a
      // real failure, not a stale-chunk race. Let it surface to the error
      // boundary (and Sentry) rather than looping.
      return;
    }

    try {
      sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    } catch {
      // ignore — see read note above
    }

    // Suppress Vite's rethrow so the rejection doesn't also hit the error
    // boundary; we're navigating away regardless.
    event.preventDefault();
    window.location.reload();
  });
}
